import React, { useState, useEffect } from 'react';
import { Plus, Search, Edit2, Trash2, Globe, MapPin, TrendingUp, DollarSign, Calendar, BookOpen, User, CheckCircle, FileText, ChevronLeft, ChevronRight, Menu, X, Save } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

interface University {
  id: string;
  name: string;
  country: string;
  city: string;
  ranking: number;
  website: string;
  description: string;
  requirements: string;
  tuition_fee_min: number;
  tuition_fee_max: number;
  currency: string;
  application_deadline: string;
  created_at: string;
}

interface StudentApplication {
  id?: string; // ID from university_applications table if manual
  studentId: string;
  studentName: string;
  studentSurname: string;
  department: string;
  username?: string;
  password?: string;
  language_level?: string;
  missing_docs?: string;
  payment_status?: string;
  app_status?: string;
  result?: string;
  enrollment_status?: string;
  universitaly_status?: string;
  note?: string;
  comment?: string;
  post?: string;
  service_status?: string;
  isDirty?: boolean;
  source: 'parsed' | 'manual';
}

interface OperationStudent {
  id: string;
  first_name: string;
  last_name: string;
}

const UniversitiesPage: React.FC = () => {
  const { user } = useAuth();
  const [universities, setUniversities] = useState<University[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showAddStudentModal, setShowAddStudentModal] = useState(false);
  const [editingUniversity, setEditingUniversity] = useState<University | null>(null);
  const [selectedUniversity, setSelectedUniversity] = useState<University | null>(null);
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);

  // Student Data
  const [universityStudents, setUniversityStudents] = useState<StudentApplication[]>([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchUniversities();
  }, []);

  useEffect(() => {
    if (selectedUniversity) {
      fetchStudentsForUniversity(selectedUniversity);
    } else {
      setUniversityStudents([]);
    }
  }, [selectedUniversity]);

  const fetchUniversities = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('universities')
        .select('*')
        .order('ranking', { ascending: true, nullsFirst: false });

      if (error) throw error;
      setUniversities(data || []);
    } catch (err) {
      console.error('Error fetching universities:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchStudentsForUniversity = async (uni: University) => {
    setStudentsLoading(true);
    try {
      // 1. Fetch Legacy Data (Parsed from Notes)
      let operationStudentsQuery = supabase
        .from('operation_students')
        .select('*');

      if (user?.branchId) operationStudentsQuery = operationStudentsQuery.eq('branch_id', user.branchId);
      const { data: opStudents, error: opError } = await operationStudentsQuery;

      if (opError) throw opError;

      const parsedStudents: StudentApplication[] = [];

      opStudents?.forEach((student: any) => {
        const notes = student.notes || '';
        if (notes.includes(uni.name)) {
          const appsPart = notes.split('--- BAŞVURULAR ---')[1]?.split('---')[0];
          if (appsPart) {
            const lines = appsPart.split('\n').filter((l: string) => l.includes(uni.name));
            lines.forEach((line: string) => {
              const parts = line.split(' - ');
              const dept = parts[1]?.split('(')[0]?.trim();
              const result = parts[1]?.match(/\((.*)\)/)?.[1];

              parsedStudents.push({
                studentId: student.id,
                studentName: student.first_name,
                studentSurname: student.last_name,
                department: dept || '-',
                username: '',
                password: '',
                language_level: 'IELTS 6.5', // Default placeholder for parsed
                missing_docs: 'TAM',
                payment_status: '30 EURO ÖDENDİ',
                app_status: 'TAMAMLANDI',
                result: result || 'Bekliyor',
                enrollment_status: '-',
                universitaly_status: '-',
                note: '',
                comment: '',
                post: '',
                service_status: '',
                source: 'parsed'
              });
            });
          }
        }
      });

      // 2. Fetch Manual Data (from university_applications table)
      const { data: manualApps, error: manualError } = await supabase
        .from('university_applications')
        .select(`
          *,
          students (
            first_name,
            last_name
          )
        `)
        .eq('branch_id', user?.branchId)
        .eq('university_id', uni.id);

      if (manualError && manualError.code !== '42P01') { // Ignore table not found for seamless fallback
        console.error('Error fetching manual applications:', manualError);
      }

      const manualStudents: StudentApplication[] = (manualApps || []).map((app: any) => ({
        id: app.id,
        studentId: app.student_id,
        studentName: app.student_name || app.students?.first_name || 'Bilinmiyor',
        studentSurname: app.student_surname || app.students?.last_name || '',
        department: app.department,
        username: app.username,
        password: app.password,
        language_level: app.language_level,
        missing_docs: app.missing_docs,
        payment_status: app.payment_status,
        app_status: app.application_status,
        result: app.result,
        enrollment_status: app.enrollment_status,
        universitaly_status: app.universitaly_status,
        note: app.note,
        comment: app.comment,
        post: app.post,
        service_status: app.italy_service_status,
        source: 'manual'
      }));

      // Filter parsed students to exclude those that are already in manual list (prioritize manual db record)
      const filteredParsedStudents = parsedStudents.filter(p => {
        return !manualStudents.some(m =>
          m.studentId &&
          m.studentId === p.studentId &&
          m.department === p.department
        );
      });

      setUniversityStudents([...filteredParsedStudents, ...manualStudents]);

    } catch (err) {
      console.error('Error fetching students:', err);
    } finally {
      setStudentsLoading(false);
    }
  }

  const handleStudentChange = (index: number, field: keyof StudentApplication, value: string) => {
    const newStudents = [...universityStudents];
    newStudents[index] = { ...newStudents[index], [field]: value, isDirty: true };
    setUniversityStudents(newStudents);
  };

  const handleSaveAll = async () => {
    if (!selectedUniversity) return;
    const dirtyStudents = universityStudents.filter(s => s.isDirty);

    if (dirtyStudents.length === 0) {
      alert('Kaydedilecek değişiklik bulunamadı.');
      return;
    }

    setSaving(true);
    try {
      for (const std of dirtyStudents) {
        const payload = {
          university_id: selectedUniversity.id,
          student_id: std.studentId || null,
          student_name: std.studentName,
          student_surname: std.studentSurname,
          department: std.department,
          username: std.username,
          password: std.password,
          language_level: std.language_level,
          missing_docs: std.missing_docs,
          payment_status: std.payment_status,
          application_status: std.app_status,
          result: std.result,
          enrollment_status: std.enrollment_status,
          universitaly_status: std.universitaly_status,
          note: std.note,
          comment: std.comment,
          post: std.post,
          italy_service_status: std.service_status,
          updated_at: new Date().toISOString()
        };

        if (std.id && std.source === 'manual') {
          // Update existing
          await supabase.from('university_applications').update(payload).eq('id', std.id);
        } else {
          // Insert new (migrating parsed -> manual or just new entry)
          // We could check if one exists with same student_id + university_id to avoid dupes, but for now we trust the flow.
          await supabase.from('university_applications').insert(payload);
        }
      }
      alert('Değişiklikler başarıyla kaydedildi.');
      fetchStudentsForUniversity(selectedUniversity);
    } catch (e) {
      console.error('Save error:', e);
      alert('Kaydetme sırasında hata oluştu.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!await window.appConfirm('Bu üniversiteyi silmek istediğinizden emin misiniz?')) return;

    try {
      const { error } = await supabase
        .from('universities')
        .delete()
        .eq('id', id);

      if (error) throw error;
      fetchUniversities();
      if (selectedUniversity?.id === id) setSelectedUniversity(null);
    } catch (err) {
      console.error('Error deleting university:', err);
      alert('Üniversite silinirken hata oluştu');
    }
  };

  const handleDeleteStudent = async (student: StudentApplication, e: React.MouseEvent) => {
    e.stopPropagation();
    if (!await window.appConfirm(`${student.studentName} ${student.studentSurname} adlı öğrenciyi bu üniversiteden silmek istediğinize emin misiniz?`)) return;

    try {
      if (student.source === 'manual' && student.id) {
        const { error } = await supabase
          .from('university_applications')
          .delete()
          .eq('id', student.id);

        if (error) throw error;
      } else {
        // Parsed from notes - tough to delete accurately without ID, 
        // but we can try to update the operation_students notes to remove the line.
        // For now, we will guide the user to Edit Student modal since parsing is read-only logic effectively.
        alert('Notlardan çekilen (Eski/Otomatik) öğrenci kayıtları buradan silinemez. Lütfen öğrenci detay kartından ilgili satırı siliniz.');
        return;
      }

      alert('Öğrenci kaydı silindi.');
      if (selectedUniversity) fetchStudentsForUniversity(selectedUniversity);

    } catch (error) {
      console.error('Error deleting student application:', error);
      alert('Silme işleminde hata oluştu.');
    }
  };

  const filteredUniversities = universities.filter(uni =>
    uni.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    uni.city?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="flex flex-col md:flex-row h-[calc(100vh-2rem)] bg-gray-50/50 dark:bg-gray-950 font-sans overflow-hidden">

      {/* Sidebar: University List */}
      <div className={`relative flex flex-col border-r border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 z-10 transition-all duration-300 ${isSidebarCollapsed ? 'w-16' : 'w-full md:w-80'}`}>
        {/* Toggle Button for University Sidebar */}
        <button
          onClick={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
          className="absolute -right-3 top-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-full p-1 shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors z-50 text-gray-500"
        >
          {isSidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>

        <div className="p-4 border-b border-gray-100 dark:border-gray-800 bg-white dark:bg-gray-900 sticky top-0 z-20">
          <div className={`flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'} mb-4`}>
            {!isSidebarCollapsed && <h2 className="text-xl font-extrabold text-gray-800 dark:text-white truncate">Üniversiteler</h2>}
            <button
              onClick={() => setShowAddModal(true)}
              className="p-2 bg-blue-50 text-blue-600 rounded-lg hover:bg-blue-100 transition-colors"
              title="Yeni Üniversite Ekle"
            >
              <Plus className="w-5 h-5" />
            </button>
          </div>

          {!isSidebarCollapsed && (
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
              />
            </div>
          )}
        </div>

        <div className="flex-1 overflow-y-auto p-2 space-y-1 overflow-x-hidden">
          {loading ? (
            <div className="text-center py-10 text-gray-500 text-sm">...</div>
          ) : (
            filteredUniversities.map(uni => (
              <div
                key={uni.id}
                onClick={() => setSelectedUniversity(uni)}
                className={`group flex items-center ${isSidebarCollapsed ? 'justify-center' : 'justify-between'} p-3 rounded-xl cursor-pointer transition-all ${selectedUniversity?.id === uni.id
                  ? 'bg-blue-600 text-white shadow-lg shadow-blue-500/30'
                  : 'hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300'
                  }`}
                title={isSidebarCollapsed ? uni.name : undefined}
              >
                <div className="flex items-center gap-3 truncate">
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${selectedUniversity?.id === uni.id ? 'bg-white/20' : 'bg-gray-100 dark:bg-gray-800'
                    }`}>
                    <BookOpen className="w-4 h-4" />
                  </div>
                  {!isSidebarCollapsed && (
                    <div className="truncate">
                      <div className="font-bold text-sm truncate">{uni.name}</div>
                      <div className={`text-xs flex items-center gap-1 ${selectedUniversity?.id === uni.id ? 'text-blue-100' : 'text-gray-400'
                        }`}>
                        <MapPin className="w-3 h-3" />
                        {uni.city}, {uni.country}
                      </div>
                    </div>
                  )}
                </div>
                {!isSidebarCollapsed && (
                  <div className="flex items-center opacity-0 group-hover:opacity-100 transition-opacity gap-1">
                    <button
                      onClick={(e) => { e.stopPropagation(); setEditingUniversity(uni); }}
                      className={`p-1.5 rounded-lg ${selectedUniversity?.id === uni.id ? 'hover:bg-white/20' : 'hover:bg-gray-200 dark:hover:bg-gray-700'}`}
                    >
                      <Edit2 className="w-3.5 h-3.5" />
                    </button>
                    <button
                      onClick={(e) => handleDelete(uni.id, e)}
                      className={`p-1.5 rounded-lg ${selectedUniversity?.id === uni.id ? 'hover:bg-white/20' : 'hover:bg-red-100 text-red-500'}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main Content: Student List */}
      <div className="flex-1 flex flex-col h-full overflow-hidden bg-gray-50 dark:bg-gray-950">
        {selectedUniversity ? (
          <>
            {/* Header */}
            <div className="px-8 py-6 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 shadow-sm flex items-center justify-between shrink-0">
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-black text-gray-900 dark:text-white uppercase tracking-tight">
                    {selectedUniversity.name}
                  </h1>
                  {selectedUniversity.application_deadline && (
                    <span className="px-3 py-1 bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-400 text-xs font-bold rounded-full border border-rose-200 dark:border-rose-800">
                      DEADLINE: {new Date(selectedUniversity.application_deadline).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long' }).toUpperCase()}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  {[selectedUniversity.city, selectedUniversity.country].filter(Boolean).join(', ')}
                  {selectedUniversity.ranking && (
                    <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400 font-semibold px-2 py-0.5 bg-amber-50 dark:bg-amber-900/20 rounded-md">
                      <TrendingUp className="w-3.5 h-3.5" /> #{selectedUniversity.ranking}
                    </span>
                  )}
                </div>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleSaveAll}
                  disabled={saving}
                  className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-indigo-500/20 transition-all disabled:opacity-50"
                >
                  <Save className="w-4 h-4" />
                  {saving ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
                </button>
                <button
                  onClick={() => setShowAddStudentModal(true)}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-blue-500/20 transition-all"
                >
                  <Plus className="w-4 h-4" />
                  Öğrenci Ekle
                </button>
                <button className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-sm font-bold shadow-lg shadow-emerald-500/20 transition-all">
                  <FileText className="w-4 h-4" />
                  Excel İndir
                </button>
              </div>
            </div>

            {/* Table Content */}
            <div className="flex-1 overflow-auto p-6">
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm text-left whitespace-nowrap">
                    <thead className="bg-gray-50 dark:bg-gray-800 text-gray-500 dark:text-gray-400 font-bold text-xs uppercase tracking-wider border-b border-gray-100 dark:border-gray-700">
                      <tr>
                        <th className="px-4 py-4 text-center w-10 sticky left-0 bg-gray-50 dark:bg-gray-800 z-10">#</th>
                        <th className="px-4 py-4 min-w-[150px]">Öğrenci Adı</th>
                        <th className="px-4 py-4 min-w-[150px]">Soyadı</th>
                        <th className="px-4 py-4 min-w-[200px]">Bölüm Tercihi</th>
                        <th className="px-4 py-4 min-w-[150px]">Hesap</th>
                        <th className="px-4 py-4 min-w-[150px]">Şifre</th>
                        <th className="px-4 py-4 min-w-[100px]">Dil</th>
                        <th className="px-4 py-4 min-w-[200px]">Eksik Evraklar</th>
                        <th className="px-4 py-4 min-w-[220px]">Başvuru Ödeme</th>
                        <th className="px-4 py-4 min-w-[150px]">Başvuru Durumu</th>
                        <th className="px-4 py-4 min-w-[120px]">Sonuç</th>
                        <th className="px-4 py-4 min-w-[150px]">Kayıt Durumu</th>
                        <th className="px-4 py-4 min-w-[150px]">Universitaly P-Enroll</th>
                        <th className="px-4 py-4 min-w-[200px]">Not</th>
                        <th className="px-4 py-4 min-w-[200px]">Yorum</th>
                        <th className="px-4 py-4 min-w-[150px]">Post</th>
                        <th className="px-4 py-4 min-w-[150px]">İtalya Hiz.</th>
                        <th className="px-4 py-4 w-10 sticky right-0 bg-gray-50 dark:bg-gray-800 z-10">Sil</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                      {studentsLoading ? (
                        <tr>
                          <td colSpan={18} className="px-6 py-12 text-center text-gray-500">
                            Yükleniyor...
                          </td>
                        </tr>
                      ) : universityStudents.length > 0 ? (
                        universityStudents.map((std, index) => (
                          <tr key={index} className="hover:bg-blue-50/50 dark:hover:bg-blue-900/10 transition-colors">
                            <td className="px-4 py-3 text-center font-bold text-gray-400">{index + 1}</td>
                            <td className="px-4 py-3">
                              <input
                                value={std.studentName}
                                onChange={(e) => handleStudentChange(index, 'studentName', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full font-medium text-gray-900 dark:text-white"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.studentSurname}
                                onChange={(e) => handleStudentChange(index, 'studentSurname', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full font-medium text-gray-900 dark:text-white"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.department}
                                onChange={(e) => handleStudentChange(index, 'department', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full text-blue-600 dark:text-blue-400 font-medium"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.username || ''}
                                onChange={(e) => handleStudentChange(index, 'username', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full font-mono text-xs text-gray-600 dark:text-gray-300"
                                placeholder="-"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.password || ''}
                                onChange={(e) => handleStudentChange(index, 'password', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full font-mono text-xs text-gray-600 dark:text-gray-300"
                                placeholder="******"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.language_level || ''}
                                onChange={(e) => handleStudentChange(index, 'language_level', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full text-xs font-bold text-amber-800 dark:text-amber-400"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.missing_docs || ''}
                                onChange={(e) => handleStudentChange(index, 'missing_docs', e.target.value)}
                                className={`bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full text-xs font-bold ${std.missing_docs === 'TAM' ? 'text-emerald-600' : 'text-red-600'}`}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <select
                                  value={std.payment_status === 'ÖDENMEDİ' ? 'ÖDENMEDİ' : 'ÖDENDİ'}
                                  onChange={(e) => {
                                    if (e.target.value === 'ÖDENMEDİ') {
                                      handleStudentChange(index, 'payment_status', 'ÖDENMEDİ');
                                    } else {
                                      handleStudentChange(index, 'payment_status', '');
                                    }
                                  }}
                                  className={`bg-transparent text-xs font-bold border-none focus:ring-0 cursor-pointer p-0 pr-2 ${std.payment_status === 'ÖDENMEDİ' ? 'text-red-600' : 'text-green-600'}`}
                                >
                                  <option value="ÖDENMEDİ" className="text-red-600">Ödenmedi</option>
                                  <option value="ÖDENDİ" className="text-green-600">Ödendi</option>
                                </select>
                                {std.payment_status !== 'ÖDENMEDİ' && (
                                  <input
                                    value={std.payment_status || ''}
                                    onChange={(e) => handleStudentChange(index, 'payment_status', e.target.value)}
                                    className="bg-transparent border-b border-gary-200 dark:border-gray-700 focus:border-blue-500 outline-none w-full text-xs text-green-700 dark:text-green-400 font-bold"
                                    placeholder="Tutar"
                                  />
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.app_status || ''}
                                onChange={(e) => handleStudentChange(index, 'app_status', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full text-green-700 dark:text-green-400 text-xs font-bold"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.result || ''}
                                onChange={(e) => handleStudentChange(index, 'result', e.target.value)}
                                className={`bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full text-xs font-bold ${std.result === 'Kabul' ? 'text-blue-700' : std.result === 'Red' ? 'text-red-700' : 'text-gray-600'}`}
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.enrollment_status || ''}
                                onChange={(e) => handleStudentChange(index, 'enrollment_status', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full text-gray-500 text-xs"
                                placeholder="-"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.universitaly_status || ''}
                                onChange={(e) => handleStudentChange(index, 'universitaly_status', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full text-gray-500 text-xs"
                                placeholder="-"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.note || ''}
                                onChange={(e) => handleStudentChange(index, 'note', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full text-gray-500 text-xs truncate"
                                placeholder="-"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.comment || ''}
                                onChange={(e) => handleStudentChange(index, 'comment', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full text-gray-500 text-xs"
                                placeholder="-"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.post || ''}
                                onChange={(e) => handleStudentChange(index, 'post', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full text-gray-500 text-xs"
                                placeholder="-"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <input
                                value={std.service_status || ''}
                                onChange={(e) => handleStudentChange(index, 'service_status', e.target.value)}
                                className="bg-transparent border-b border-transparent focus:border-blue-500 outline-none w-full text-gray-500 text-xs"
                                placeholder="-"
                              />
                            </td>
                            <td className="px-4 py-3 sticky right-0 bg-white dark:bg-gray-900 border-l border-gray-100 dark:border-gray-800">
                              <button
                                onClick={(e) => handleDeleteStudent(std, e)}
                                className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                title="Öğrenci Kaydını Sil"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan={18} className="px-6 py-20 text-center">
                            <div className="flex flex-col items-center justify-center text-gray-400">
                              <User className="w-12 h-12 mb-3 bg-gray-50 rounded-full p-2" />
                              <span className="font-medium">Bu üniversite için öğrenci kaydı bulunamadı.</span>
                            </div>
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <div className="w-32 h-32 bg-gray-100 dark:bg-gray-900 rounded-full flex items-center justify-center mb-6">
              <BookOpen className="w-16 h-16 text-gray-300 dark:text-gray-700" />
            </div>
            <h3 className="text-xl font-bold text-gray-800 dark:text-white">Bir Üniversite Seçin</h3>
            <p className="max-w-xs text-center mt-2 text-gray-500">Öğrenci listesini ve detayları görmek için soldaki menüden bir üniversite seçin.</p>
          </div>
        )}
      </div>

      {(showAddModal || editingUniversity) && (
        <UniversityModal
          university={editingUniversity}
          onClose={() => {
            setShowAddModal(false);
            setEditingUniversity(null);
          }}
          onSave={() => {
            fetchUniversities();
            setShowAddModal(false);
            setEditingUniversity(null);
          }}
        />
      )}

      {/* Add Manual Student Modal */}
      {showAddStudentModal && selectedUniversity && (
        <AddStudentModal
          universityId={selectedUniversity.id}
          universityName={selectedUniversity.name}
          onClose={() => setShowAddStudentModal(false)}
          onSave={() => {
            setShowAddStudentModal(false);
            if (selectedUniversity) fetchStudentsForUniversity(selectedUniversity);
          }}
        />
      )}
    </div>
  );
};

interface UniversityModalProps {
  university: University | null;
  onClose: () => void;
  onSave: () => void;
}

const UniversityModal: React.FC<UniversityModalProps> = ({ university, onClose, onSave }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    name: university?.name || '',
    country: university?.country || '',
    city: university?.city || '',
    ranking: university?.ranking || null,
    website: university?.website || '',
    description: university?.description || '',
    requirements: university?.requirements || '',
    tuition_fee_min: university?.tuition_fee_min || null,
    tuition_fee_max: university?.tuition_fee_max || null,
    currency: university?.currency || 'EUR',
    application_deadline: university?.application_deadline || '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      if (university) {
        const { error } = await supabase
          .from('universities')
          .update({ ...formData, updated_at: new Date().toISOString() })
          .eq('id', university.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('universities')
          .insert([{ ...formData, created_by: user?.id }]);

        if (error) throw error;
      }

      onSave();
    } catch (err) {
      console.error('Error saving university:', err);
      alert('Üniversite kaydedilirken hata oluştu');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all">
      <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8 border-b border-gray-100 dark:border-gray-700 pb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                {university ? 'Üniversite Düzenle' : 'Yeni Üniversite'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {university ? 'Mevcut üniversite bilgilerini güncelleyin' : 'Sisteme yeni bir üniversite ekleyin'}
              </p>
            </div>
            <div className="w-12 h-12 bg-blue-50 dark:bg-blue-900/30 rounded-2xl flex items-center justify-center text-blue-600 dark:text-blue-400">
              <BookOpen className="w-6 h-6" />
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Üniversite Adı *
              </label>
              <input
                type="text"
                required
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                placeholder="Örn: Politecnico di Milano"
              />
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Ülke *
                </label>
                <input
                  type="text"
                  required
                  value={formData.country}
                  onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                  placeholder="Örn: İtalya"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Şehir
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                  placeholder="Örn: Milano"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Sıralama
                </label>
                <input
                  type="number"
                  value={formData.ranking || ''}
                  onChange={(e) => setFormData({ ...formData, ranking: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                  placeholder="Dünya sıralaması"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Web Sitesi
                </label>
                <input
                  type="url"
                  value={formData.website}
                  onChange={(e) => setFormData({ ...formData, website: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                  placeholder="https://..."
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Açıklama
              </label>
              <textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={3}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all resize-none"
                placeholder="Üniversite hakkında kısa bilgi..."
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Gereksinimler
              </label>
              <textarea
                value={formData.requirements}
                onChange={(e) => setFormData({ ...formData, requirements: e.target.value })}
                rows={2}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all resize-none"
                placeholder="Başvuru şartları..."
              />
            </div>

            <div className="grid grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Min. Ücret
                </label>
                <input
                  type="number"
                  value={formData.tuition_fee_min || ''}
                  onChange={(e) => setFormData({ ...formData, tuition_fee_min: e.target.value ? parseFloat(e.target.value) : null })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Max. Ücret
                </label>
                <input
                  type="number"
                  value={formData.tuition_fee_max || ''}
                  onChange={(e) => setFormData({ ...formData, tuition_fee_max: e.target.value ? parseFloat(e.target.value) : null })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Para Birimi
                </label>
                <select
                  value={formData.currency}
                  onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
                >
                  <option value="EUR">EUR</option>
                  <option value="USD">USD</option>
                  <option value="GBP">GBP</option>
                  <option value="TRY">TRY</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                Son Başvuru Tarihi
              </label>
              <input
                type="date"
                value={formData.application_deadline}
                onChange={(e) => setFormData({ ...formData, application_deadline: e.target.value })}
                className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 transition-all"
              />
            </div>

            <div className="flex justify-end gap-3 pt-6 border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
              >
                İptal
              </button>
              <button
                type="submit"
                className="px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors shadow-lg shadow-blue-500/30"
              >
                {university ? 'Güncelle' : 'Ekle'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

interface AddStudentModalProps {
  universityId: string;
  universityName: string;
  onClose: () => void;
  onSave: () => void;
}

const AddStudentModal: React.FC<AddStudentModalProps> = ({ universityId, universityName, onClose, onSave }) => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);

  // Initial state updated to include all fields requested
  const [formData, setFormData] = useState({
    studentName: '',
    studentSurname: '',
    department: '',
    username: '',
    password: '',
    language_level: '',
    missing_docs: '',
    payment_status: '',
    status: 'Başvuru Yapıldı',
    result: '',
    enrollment_status: '',
    universitaly_status: '',
    note: '',
    comment: '',
    post: '',
    service_status: ''
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.studentName || !formData.studentSurname) {
      alert('Lütfen öğrenci adı ve soyadını giriniz.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase
        .from('university_applications')
        .insert([{
          university_id: universityId,
          student_id: null, // Manuel giriş olduğu için null
          student_name: formData.studentName,
          student_surname: formData.studentSurname,
          department: formData.department,
          username: formData.username,
          password: formData.password,
          language_level: formData.language_level,
          missing_docs: formData.missing_docs,
          payment_status: formData.payment_status,
          application_status: formData.status,
          result: formData.result,
          enrollment_status: formData.enrollment_status,
          universitaly_status: formData.universitaly_status,
          note: formData.note,
          comment: formData.comment,
          post: formData.post,
          italy_service_status: formData.service_status,
          created_by: user?.id
        }]);

      if (error) throw error;
      onSave();
    } catch (err) {
      console.error('Error adding student:', err);
      alert('Öğrenci eklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4 transition-all">
      <div className="bg-white dark:bg-gray-800 rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-y-auto shadow-2xl animate-in zoom-in-95 duration-200">
        <div className="p-8">
          <div className="flex items-center justify-between mb-8 border-b border-gray-100 dark:border-gray-700 pb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                Öğrenci Ekle
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {universityName}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full">
              <X className="w-6 h-6 text-gray-500" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Öğrenci Adı */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Öğrenci Adı *
                </label>
                <input
                  type="text"
                  required
                  value={formData.studentName}
                  onChange={(e) => setFormData({ ...formData, studentName: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                  placeholder="Örn: Ahmet"
                />
              </div>

              {/* Öğrenci Soyadı */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Öğrenci Soyadı *
                </label>
                <input
                  type="text"
                  required
                  value={formData.studentSurname}
                  onChange={(e) => setFormData({ ...formData, studentSurname: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                  placeholder="Örn: Yılmaz"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Bölüm Tercihi */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Bölüm Tercihi
                </label>
                <input
                  type="text"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>

              {/* Hesap */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Hesap (Kullanıcı Adı)
                </label>
                <input
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>

              {/* Şifre */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Şifre
                </label>
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Dil */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Dil
                </label>
                <input
                  type="text"
                  value={formData.language_level}
                  onChange={(e) => setFormData({ ...formData, language_level: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>

              {/* Eksik Evraklar */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Eksik Evraklar
                </label>
                <input
                  type="text"
                  value={formData.missing_docs}
                  onChange={(e) => setFormData({ ...formData, missing_docs: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>

              {/* Başvuru Ödeme */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Başvuru Ödeme
                </label>
                <input
                  type="text"
                  value={formData.payment_status}
                  onChange={(e) => setFormData({ ...formData, payment_status: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Başvuru Durumu */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Başvuru Durumu
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                >
                  <option value="Başvuru Yapıldı">Başvuru Yapıldı</option>
                  <option value="Bekleniyor">Bekleniyor</option>
                  <option value="TAMAMLANDI">TAMAMLANDI</option>
                </select>
              </div>

              {/* Sonuç */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Sonuç
                </label>
                <input
                  type="text"
                  value={formData.result}
                  onChange={(e) => setFormData({ ...formData, result: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>

              {/* Kayıt Durumu */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Kayıt Durumu
                </label>
                <input
                  type="text"
                  value={formData.enrollment_status}
                  onChange={(e) => setFormData({ ...formData, enrollment_status: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Universitaly */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Universitaly P-Enroll
                </label>
                <input
                  type="text"
                  value={formData.universitaly_status}
                  onChange={(e) => setFormData({ ...formData, universitaly_status: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>

              {/* İtalya Hiz. */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  İtalya Hizmetleri
                </label>
                <input
                  type="text"
                  value={formData.service_status}
                  onChange={(e) => setFormData({ ...formData, service_status: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>

              {/* Post */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Post
                </label>
                <input
                  type="text"
                  value={formData.post}
                  onChange={(e) => setFormData({ ...formData, post: e.target.value })}
                  className="w-full px-4 py-2.5 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Not */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Not
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm min-h-[80px] resize-none"
                />
              </div>

              {/* Yorum */}
              <div className="col-span-1">
                <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                  Yorum
                </label>
                <textarea
                  value={formData.comment}
                  onChange={(e) => setFormData({ ...formData, comment: e.target.value })}
                  className="w-full px-4 py-3 bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 rounded-lg text-sm min-h-[80px] resize-none"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium transition-colors"
              >
                İptal
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2.5 bg-blue-600 text-white rounded-xl hover:bg-blue-700 font-medium transition-colors shadow-lg shadow-blue-500/30 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Kaydediliyor...' : 'Kaydet'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default UniversitiesPage;
